import type { ReactNode } from 'react';
import { Check, ICONO } from '../ui/iconos.js';

/**
 * El cierre de un flujo (thanks): check-in enviado, reporte enviado, respuesta
 * del cuidador enviada.
 *
 * Es el momento donde la app puede tener calidez sin celebrar de más.
 *
 * - `checkin`: las dos cápsulas del isotipo dan "un paso más" (cada una entra
 *   con su demora) y aparece el tilde teal. Fondo teal-100 que se funde.
 * - `reporte`: círculo navy con el tilde y halo, fondo navy-100.
 * - `cuidador`: lo mismo en el violeta del rol.
 *
 * Las cápsulas son dos `span` con los tokens de la marca, no una imagen. Todo el
 * dibujo es decorativo (`aria-hidden`); lo que se anuncia es el título, que va
 * en un `h1` porque es lo único que hay en la pantalla. El movimiento se apaga
 * con `prefers-reduced-motion`.
 */

type Variante = 'checkin' | 'reporte' | 'cuidador';

const FONDO: Readonly<Record<Variante, string>> = Object.freeze({
  checkin: 'from-accent-soft',
  reporte: 'from-brand-soft',
  cuidador: 'from-role-cuidador-bg',
});

function MarcaDePaso() {
  return (
    <div aria-hidden="true" className="relative mb-4 h-47.5 w-42.5">
      <span className="absolute bottom-0 left-5.5 h-29.5 w-14 rounded-full border-6 border-primary motion-safe:animate-paso" />
      <span className="absolute top-0 left-21 h-32 w-16 rounded-full bg-brand motion-safe:animate-paso-2" />
      <span className="absolute right-0 bottom-2 flex size-16 items-center justify-center rounded-full border-5 border-background bg-accent text-heading shadow-glow-teal motion-safe:animate-aparecer">
        <Check className={ICONO.destacado} />
      </span>
    </div>
  );
}

function CirculoDeListo({ variante }: { variante: 'reporte' | 'cuidador' }) {
  return (
    <span
      aria-hidden="true"
      className={`mb-4 flex size-28 items-center justify-center rounded-full text-primary-foreground motion-safe:animate-aparecer ${
        variante === 'cuidador'
          ? 'bg-role-cuidador shadow-halo-purple-xl'
          : 'bg-brand shadow-halo-navy-xl'
      }`}
    >
      <Check className="size-14" strokeWidth={2.5} />
    </span>
  );
}

export function PantallaDeCierre({
  variante,
  titulo,
  texto,
  children,
}: {
  variante: Variante;
  titulo: string;
  texto: string;
  /** Lo que sigue: el próximo hito, un resumen. */
  children?: ReactNode;
}) {
  return (
    <section
      className={`flex flex-1 flex-col items-center justify-center gap-4 bg-linear-180 to-background to-62% px-6 py-8 text-center ${FONDO[variante]}`}
    >
      {variante === 'checkin' ? <MarcaDePaso /> : <CirculoDeListo variante={variante} />}
      <h1 className="m-0 font-serif text-display-xl font-regular text-heading">{titulo}</h1>
      <p className="m-0 max-w-[24ch] text-touch text-body">{texto}</p>
      {children}
    </section>
  );
}
